/**
 * Human-readable record references — TE000003, PR007896.
 *
 * These are DISPLAY identifiers, not primary keys. Every table keeps its UUIDv7 `id` for
 * referential integrity; `ref` is the thing a property manager reads down the phone, writes
 * on a bond form, and searches for. The two never substitute for each other:
 *   - joins, foreign keys, URLs from internal links  → uuid
 *   - anything a human types, reads or quotes        → ref
 *
 * Numbering is PER ORGANISATION. Each agency's first tenancy is TE000001. Refs are unique
 * within an org, which is sufficient because every query is org-scoped by RLS — but it means
 * a bare ref is ambiguous across agencies, so support tooling must always carry the org too.
 *
 * Trade-off accepted deliberately: a sequential ref leaks record counts (a client can tell
 * how many tenancies an agency manages). That is the standard bargain every accounting
 * system makes for readability. Do not "fix" it by randomising — unpredictable refs defeat
 * the entire purpose.
 */

/** Every entity that carries a public reference. */
export type RefEntity =
  | 'person'
  | 'property'
  | 'tenancy'
  | 'owner'
  | 'supplier'
  | 'bond'
  | 'bill'
  | 'invoice'
  | 'inspection'
  | 'maintenance'
  | 'work_order'
  | 'journal'
  | 'disbursement'
  | 'transaction'
  | 'statement'
  | 'reconciliation'
  | 'statement_import'
  | 'user';

/**
 * Explicit prefix registry — deliberately NOT derived from the entity name.
 *
 * "First two letters" collides as the domain grows: Invoice/Inspection both yield IN,
 * Owner/Organisation both want O-something, Bill/Bond both want B-something. A derived
 * prefix fails silently, and by the time anyone notices, the codes are printed on issued
 * documents and cannot be recalled.
 *
 * RULES FOR ADDING ONE:
 *  1. Two uppercase letters, A–Z only.
 *  2. Must not already be in this map — `assertPrefixesUnique()` enforces it, and there is
 *     a unit test that fails the build on a duplicate.
 *  3. NEVER change or reuse an existing prefix. Refs live on statements, bond lodgement
 *     forms and in customers' own records; a reused prefix silently repoints history.
 */
export const REF_PREFIX: Record<RefEntity, string> = {
  person: 'PE',
  property: 'PR',
  tenancy: 'TE',
  owner: 'OW',
  supplier: 'SU',
  bond: 'BD',
  bill: 'BL',
  invoice: 'IV',
  inspection: 'IS',
  maintenance: 'MN',
  work_order: 'WO',
  journal: 'JN',
  disbursement: 'DB',
  transaction: 'TX',
  statement: 'ST',
  reconciliation: 'RC',
  statement_import: 'SI',
  /*
   * A member of staff, as their own agency numbers them.
   *
   * ALLOCATED PER MEMBERSHIP, NOT PER LOGIN, and that is forced by what a ref is: refs come from
   * `ref_counters`, whose primary key is `(org_id, entity)`, so every series in this product is
   * an AGENCY's series. `users` has no `org_id` — a login is global and can hold memberships in
   * several organisations, which is what franchises and ProManage's own support access are built
   * on — so a number stored there would either be one global counter (giving one agency
   * US000004 and US000119, which reads as broken and leaks how many logins the platform has) or
   * no counter at all.
   *
   * So the number belongs to the person's PLACE IN AN AGENCY. Somebody who works for two
   * franchisees is US000006 in one and US000013 in the other, exactly as a property is numbered
   * inside the agency that manages it.
   */
  user: 'US',
};

/** Minimum digits. Numbers beyond this widen rather than break — 1,000,000 → TE1000000. */
export const REF_PAD = 6;

const PREFIX_RE = /^[A-Z]{2}$/;

/**
 * Fails fast at module load if two entities share a prefix. A collision must be a build
 * failure, never a runtime surprise, because the damage is unrecoverable once refs issue.
 */
export function assertPrefixesUnique(): void {
  const seen = new Map<string, RefEntity>();
  for (const [entity, prefix] of Object.entries(REF_PREFIX) as [RefEntity, string][]) {
    if (!PREFIX_RE.test(prefix)) {
      throw new Error(`Ref prefix for "${entity}" must be two uppercase letters, got "${prefix}"`);
    }
    const existing = seen.get(prefix);
    if (existing) {
      throw new Error(`Ref prefix "${prefix}" is used by both "${existing}" and "${entity}"`);
    }
    seen.set(prefix, entity);
  }
}

assertPrefixesUnique();

/** Reverse lookup, built once. */
const ENTITY_BY_PREFIX = new Map<string, RefEntity>(
  (Object.entries(REF_PREFIX) as [RefEntity, string][]).map(([entity, prefix]) => [prefix, entity]),
);

/**
 * Build a ref from an entity and its per-org sequence number.
 * `formatRef('tenancy', 3)` → `'TE000003'`
 */
export function formatRef(entity: RefEntity, sequence: number): string {
  if (!Number.isInteger(sequence) || sequence < 1) {
    throw new RangeError(`Ref sequence must be a positive integer, got ${sequence}`);
  }
  return `${REF_PREFIX[entity]}${String(sequence).padStart(REF_PAD, '0')}`;
}

export interface ParsedRef {
  entity: RefEntity;
  sequence: number;
}

/**
 * Parse a ref back to its entity and number, or null if it is not one.
 *
 * Tolerant of how a human types it: lowercase, surrounding whitespace, and an optional
 * separator (`TE-000003`, `te 3`) all parse. A search box that rejects "te3" because the
 * user did not pad it is a support ticket waiting to happen.
 */
export function parseRef(input: string): ParsedRef | null {
  const match = /^([A-Za-z]{2})[\s-]?(\d{1,12})$/.exec(input.trim());
  if (!match) return null;

  const [, prefix, digits] = match;
  // Unreachable while both groups are required, and the honest way to say so: this function
  // already returns null for anything it cannot parse, so an unexpected shape has an answer.
  if (prefix === undefined || digits === undefined) return null;

  const entity = ENTITY_BY_PREFIX.get(prefix.toUpperCase());
  if (!entity) return null;

  const sequence = Number(digits);
  if (!Number.isSafeInteger(sequence) || sequence < 1) return null;

  return { entity, sequence };
}

/** True when the input looks like a ref for the given entity. */
export function isRefFor(input: string, entity: RefEntity): boolean {
  return parseRef(input)?.entity === entity;
}

/**
 * Normalise anything ref-shaped to canonical form, for storing a user's search term or
 * comparing two hand-typed values. Returns null when the input is not a ref.
 */
export function normaliseRef(input: string): string | null {
  const parsed = parseRef(input);
  return parsed ? formatRef(parsed.entity, parsed.sequence) : null;
}
